import React from 'react';
import { Formik, Form, Field } from 'formik';
import { stripWhitespace } from 'src/helpers/formatting';
import { BlueTick } from 'src/assets/images';
import { FloatingInput, FloatingInputVersionTwo, RadioButton } from 'src/components';
import { IdentityTypeDirectors } from 'src/features/common';
import { Modal } from 'src/components/index';
import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import ShowIf from '../show-if/show-if';
import { OnboardingDocumentType } from 'src/helpers/constants';
import { isValidFileName } from 'src/helpers/validation';
import { FlexWrap } from 'src/common/container/FlexWrap';
import './classify-id-modal.scss';

export default function ClassifyIdModal(props) {
  const getIdType = (docType) => {
    switch (docType) {
      case OnboardingDocumentType.NEW_IDENTITY_CARD_TYPE:
      case OnboardingDocumentType.OLD_GREEN_IDENTITY_DOCUMENT_TYPE:
      case 'ID':
        return 'an ID';

      case OnboardingDocumentType.PASSPORT_TYPE:
      case OnboardingDocumentType.WORK_PERMIT_TYPE:
      case OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE:
        return 'a Passport';

      case OnboardingDocumentType.ASYLUM_SEEKER:
        return 'an Asylum Seeker';

      case OnboardingDocumentType.REFUGEE_LETTER:
        return 'a Refugee';

      default:
        return 'an Identity';
    }
  };

  function validateForm(values) {
    const errors = {};

    if (props.ids && props.ids.length > 0) {
      const invalidId = !values.selectedId || values.selectedId === '';
      if (invalidId) {
        errors.selectedId = `Please select ${getIdType(values.idType)} number from the list`;
      }
    }

    if (props.isWebcamUpload && values.allowDocumentNameUpdate) {
      const invalidFileName = !values.documentName;
      if (invalidFileName) {
        errors.documentName = 'Please enter a document name';
      } else if (!isValidFileName(values.documentName)) {
        errors.documentName = 'Please enter a valid document name';
      }
    }

    return errors;
  }

  function isFormValid(values) {
    return Object.keys(validateForm(values)).length === 0;
  }

  async function submitForm(values, { resetForm }) {
    if (values.selectedId.id_type === IdentityTypeDirectors.PASSPORT) {
      values.idType = props.documentType;

      if (!props.isForeignNatonalDocument) {
        values.idType = OnboardingDocumentType.PASSPORT_TYPE;
      }
    }

    resetForm({ selectedId: '', idType: 'Old Green Identity Document' });

    const documentName = values.documentName ? `${values.documentName}.jpeg` : undefined;

    props.onSubmission(
      props.fileNames,
      stripWhitespace(values.selectedId.id_number || values.selectedId),
      values.idType,
      documentName,
    );
    props.onHide();
  }

  let idList = [];
  if (props.ids?.filter((val) => val.id_number !== undefined).length > 0) {
    idList = props.ids.map((id) => ({
      id_type: id.id_type,
      id_number: id.id_number.replace(/^(\d{6})(\d{4})(\d{3})$/, '$1 $2 $3'),
    }));
  }

  if (props.fileNames.find((fileName) => fileName.endsWith('.png'))) {
    idList = idList.filter((id) => id.id_type !== IdentityTypeDirectors.PASSPORT);
  }

  function getInitialValues() {
    const initialValues = {};

    if (props.isWebcamUpload && props?.fileNames?.length === 1) {
      const fileName = props.fileNames[0];
      initialValues.allowDocumentNameUpdate = true;
      initialValues.documentName = fileName === 'TBD' ? '' : fileName;
    } else {
      initialValues.allowDocumentNameUpdate = false;
    }

    if (props.ids && props.ids.length > 0) {
      initialValues.selectedId = props.ids[0];
      initialValues.idType = props.isForeignNatonalDocument
        ? props.documentType
        : 'Old Green Identity Document';
    }

    return initialValues;
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validate={validateForm}
      onSubmit={submitForm}
      validateOnChange={true}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <Modal
            show={props.show}
            cardStyle="ClassifyIdModal"
            headerStyle="has-text-centered"
            bodyStyle="modal-body has-text-centered has-padding-x-2 has-mobile-padding-x-6 overflow-visible"
            footerStyle="classify-footer has-text-centered"
            backdropStyle="backdrop"
            header={() => (
              <>
                <img className="has-padding-top-4" src={BlueTick} />
                <p className="successText">{props.documentTypeName} added</p>
                <ShowIf condition={!values.allowDocumentNameUpdate}>
                  <div className="has-padding-top-6">
                    {props.fileNames.map((fileName) => {
                      return (
                        <p key={fileName} className="file-name-text has-padding-x-6">
                          {fileName}
                        </p>
                      );
                    })}
                  </div>
                </ShowIf>
              </>
            )}
            body={() => (
              <>
                <ShowIf condition={props.isWebcamUpload && values.allowDocumentNameUpdate}>
                  <p className="has-padding-y-6" id="basic-text">
                    Document details
                  </p>

                  <Field
                    name="documentName"
                    type="text"
                    placeholder="Enter document name"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    maxLength={255}
                    fieldType="standard"
                    className="top-field"
                    automationId={`${props.automationIdPrefix}_Input_Document_Name`}
                  />
                </ShowIf>

                <ShowIf condition={props.ids && props.ids.length > 0}>
                  <Field
                    name="selectedId"
                    type="tel"
                    placeholder={`Select ${getIdType(values.idType)} number`}
                    items={idList}
                    getItemDisplayValue={(item) => item.id_number}
                    component={FloatingInputVersionTwo}
                    fieldType="select"
                    isDark={false}
                    className={'top-field bottom-field'}
                    automationIdPrefix={props.automationIdPrefix}
                  />
                  {!props.isForeignNatonalDocument && (
                    <FlexWrap gap="1rem">
                      {values.selectedId?.id_type === IdentityTypeDirectors.RSA_ID && (
                        <>
                          <Field
                            type="radio"
                            name="idType"
                            value="Old Green Identity Document"
                            label="ID Book"
                            component={RadioButton}
                            light={true}
                            automationId={`${props.automationIdPrefix}_Radio_Button_Group_ID_Type_Value_Old_Green_ID_Document`}
                          />
                          <Field
                            type="radio"
                            name="idType"
                            value="New Identity Card"
                            label="ID Card"
                            component={RadioButton}
                            light={true}
                            automationId={`${props.automationIdPrefix}_Radio_Button_Group_ID_Type_Value_New_Identity_Card`}
                          />
                        </>
                      )}
                    </FlexWrap>
                  )}
                </ShowIf>
              </>
            )}
            footer={() => (
              <div className="columns is-centered is-mobile">
                <div className="column">
                  <button
                    className="button is-secondary is-rounded has-margin-top-4 has-mobile-margin-top-0 "
                    onClick={() => {
                      if (!props.disabled) {
                        props.onClose(props.fileNames);
                        props.onHide();
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <div className="column">
                  <button
                    className="button is-primary is-rounded has-margin-top-4 has-mobile-margin-top-0"
                    type="submit"
                    automationid={`${props.automationIdPrefix}_Button_File_Upload_ID_Modal_Upload`}
                    disabled={isSubmitting || !isFormValid(values) || values.selectedId === '404'}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          />
        </Form>
      )}
    </Formik>
  );
}
